import { Nullable } from '@tcc/common';
import { ValueUnit } from '../types/Common';
import { ApplicationTreatment, SingleProductStats } from '../types/ApplicationTreatment';
import { toIsoDateOnly } from './dateHelpers';
import { SeasonName } from '../types/Season';

type UnifiedApplicationTreatmentMeasure = {
  u: string;
  v: number;
};

type UnifiedApplicationTreatmentProduct = {
  id: string;
  name: string;
};

export type UnifiedApplicationTreatmentProductApplied = {
  rate: UnifiedApplicationTreatmentMeasure;
  amountApplied: UnifiedApplicationTreatmentMeasure;
  area: UnifiedApplicationTreatmentMeasure;
  growerAmountUnit: Nullable<string>;
  chemicalProduct: UnifiedApplicationTreatmentProduct;
};

export type UnifiedApplicationTreatmentEvent = {
  name: string;
  firstApplicationDateTime: string;
  lastApplicationDateTime: string;
  seasonName: SeasonName;
  seasonYear: number;
  legacySeasonTag: string;
  area: UnifiedApplicationTreatmentMeasure;
  averageApplicationRate: UnifiedApplicationTreatmentMeasure;
  totalAmountApplied: UnifiedApplicationTreatmentMeasure;
  averageMachineSpeed: UnifiedApplicationTreatmentMeasure;
  chemicalProductsApplied: UnifiedApplicationTreatmentProductApplied[];
};

const normalizeUnitKey = (unit: string): string => unit.replace('km/h', 'kph');

const toValueUnit = (measure: UnifiedApplicationTreatmentMeasure): ValueUnit => ({
  v: measure.v,
  u: normalizeUnitKey(measure.u),
});

const mapProduct = (product: UnifiedApplicationTreatmentProductApplied): SingleProductStats => {
  const averageProductRate = toValueUnit(product.rate);
  const totalAmountApplied = toValueUnit(product.amountApplied);
  const growerInputProductRateUnit = product.growerAmountUnit || null;
  const productAppliedArea = toValueUnit(product.area);

  return {
    id: product.chemicalProduct.id,
    name: product.chemicalProduct.name,
    productAppliedDates: [],
    lastAppliedDate: '',
    averageProductRate,
    appliedGrowerInputUnit: growerInputProductRateUnit,
    totalAmountApplied,
    relativeArea: productAppliedArea,
    productAppliedArea,
  };
};

const toApplicationTreatment = (
  event: UnifiedApplicationTreatmentEvent,
  seasonTag: string
): ApplicationTreatment | null => {
  const name = typeof event.name === 'string' ? event.name : '';
  const start = event.firstApplicationDateTime;
  const end = event.lastApplicationDateTime;
  const appliedArea = toValueUnit(event.area);
  const averageApplicationRate = toValueUnit(event.averageApplicationRate);
  const machineSpeed = toValueUnit(event.averageMachineSpeed);
  const products = event.chemicalProductsApplied.map(mapProduct);
  const localAppliedDate = toIsoDateOnly(end) ?? '';

  return {
    season: seasonTag,
    passName: name,
    startDateTime: start,
    endDateTime: end,
    localAppliedDate,
    appliedArea,
    averageApplicationRate,
    machineSpeed,
    machineId: '',
    productStats: products,
  };
};

export const adaptUnifiedEventsToTreatments = (
  events: UnifiedApplicationTreatmentEvent[] | undefined | null,
  seasonTag: string
): ApplicationTreatment[] => {
  if (!events?.length) {
    return [];
  }

  return events
    .map((event) => toApplicationTreatment(event, seasonTag))
    .filter((event): event is ApplicationTreatment => !!event);
};
